import { decodeNormal } from './consolidation/GeomMergeTask';
/**
 * Returns true if the given position attribute descriptor matches the UNORM16 layout
 * (xyz + an oct16-encoded normal packed into a 4-component unorm16 slot, as written by
 * {@link convertToUnorm16}).
 *
 * GLTF `KHR_mesh_quantization` also produces `bytesPerItem === 2` positions, but keeps
 * `itemSize === 3` (no packed normal); the `itemSize === 4` check distinguishes the two.
 *
 * Works for both `LeanBufferGeometry` attributes and raw lmvtk mesh `vblayout` entries,
 * since both expose `{ bytesPerItem, itemSize }`.
 */
export function isUnorm16PositionLayout(pos) {
    return pos?.bytesPerItem === 2 && pos.itemSize === 4;
}
// Octahedral normal encoding helpers (Cigolle et al. 2014)
function signNotZero(v) {
    return v >= 0 ? 1 : -1;
}
function encodeOct(x, y, z) {
    const invL1Norm = 1.0 / (Math.abs(x) + Math.abs(y) + Math.abs(z));
    let ox = x * invL1Norm;
    let oy = y * invL1Norm;
    if (z <= 0) {
        const tx = ox;
        const ty = oy;
        ox = (1.0 - Math.abs(ty)) * signNotZero(tx);
        oy = (1.0 - Math.abs(tx)) * signNotZero(ty);
    }
    return [ox, oy];
}
function decodeOct(ox, oy, out) {
    let x = ox;
    let y = oy;
    const z = 1.0 - Math.abs(x) - Math.abs(y);
    if (z < 0) {
        const tx = x;
        const ty = y;
        x = (1.0 - Math.abs(ty)) * signNotZero(tx);
        y = (1.0 - Math.abs(tx)) * signNotZero(ty);
    }
    const len = Math.sqrt(x * x + y * y + z * z);
    out.x = x / len;
    out.y = y / len;
    out.z = z / len;
}
/**
 * Encode a unit normal into two snorm8 values packed into the position W component.
 * @param precise - If true, uses oct16P (floor + best-of-4 candidates). If false, uses oct16 (round).
 */
function encodeOct16(nx, ny, nz, precise = false) {
    const [ox, oy] = encodeOct(nx, ny, nz);
    // Use 127 (not 128) so that 0 maps exactly to integer 0.
    // This matches the Cigolle et al. 2014 snorm8 convention using the [-127, 127] range.
    const M = 127;
    if (!precise) {
        return [Math.round(ox * M), Math.round(oy * M)];
    }
    const sx = Math.floor(ox * M);
    const sy = Math.floor(oy * M);
    let bestX = sx, bestY = sy;
    let bestDot = -2;
    const decoded = { x: 0, y: 0, z: 0 };
    for (let i = 0; i <= 1; i++) {
        for (let j = 0; j <= 1; j++) {
            decodeOct((sx + i) / M, (sy + j) / M, decoded);
            const dot = nx * decoded.x + ny * decoded.y + nz * decoded.z;
            if (dot > bestDot) {
                bestDot = dot;
                bestX = sx + i;
                bestY = sy + j;
            }
        }
    }
    return [bestX, bestY];
}
// Mirrors decodeOct16FromUnorm in pack_normals.wgsl
export function decodeOct16(packed, out) {
    const ex = Math.max(-1, ((packed >> 8) & 0xFF) / 127.0 - 1.0);
    const ey = Math.max(-1, (packed & 0xFF) / 127.0 - 1.0);
    decodeOct(ex, ey, out);
}
export function decodeUnorm16(value) {
    return value / 65535.0 - 0.5;
}
const _tmpNormal = { x: 0, y: 0, z: 0 };
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function convertToUnorm16(mesh) {
    if (mesh.isLines || mesh.isWideLines || mesh.isPoints) {
        return;
    }
    const oldVb = mesh.vb;
    const oldStride = mesh.vbstride;
    const numVerts = oldVb.length / oldStride;
    const posLayout = mesh.vblayout.position;
    if (!posLayout) {
        return;
    }
    if (posLayout.offset !== 0) {
        console.warn('convertToUnorm16: position offset must be 0, skipping');
        return;
    }
    // If there are no normals, we could leave the w component empty, and add another flag to the geom.
    // But geometry without normals are rare, they are smaller already and the savings are smaller as well.
    // So we keep it simple and skip the compression.
    const normalLayout = mesh.vblayout.normal;
    if (!normalLayout) {
        return;
    }
    const normalOldOffset = normalLayout.offset;
    // Position shrinks from 3 to 2 float32 slots; normal slot is removed
    const newStride = oldStride - 2;
    const vbBytes = numVerts * newStride * 4;
    const ibBytes = mesh.indices ? mesh.indices.byteLength : 0;
    const ibBytesAligned = (ibBytes + 3) & ~3;
    const iblBytes = mesh.iblines ? mesh.iblines.byteLength : 0;
    // Pack vb, indices, and iblines into a single ArrayBuffer so the old one
    // can be GC'd and we don't create extra allocations via .slice().
    const buf = new ArrayBuffer(vbBytes + ibBytesAligned + iblBytes);
    const newVb = new Float32Array(buf, 0, numVerts * newStride);
    const newVbU16 = new Uint16Array(buf, 0, numVerts * newStride * 2);
    const oldVbU16 = new Uint16Array(oldVb.buffer, oldVb.byteOffset, oldVb.length * 2);
    for (let i = 0; i < numVerts; i++) {
        const oldBase = i * oldStride;
        const newBase = i * newStride;
        const u16Base = newBase * 2;
        newVbU16[u16Base] = ((oldVb[oldBase] + 0.5) * 65535 + 0.5) | 0; //  + 0.5 | 0 is faster than Math.round
        newVbU16[u16Base + 1] = ((oldVb[oldBase + 1] + 0.5) * 65535 + 0.5) | 0;
        newVbU16[u16Base + 2] = ((oldVb[oldBase + 2] + 0.5) * 65535 + 0.5) | 0;
        const nU16Base = (oldBase + normalOldOffset) * 2;
        _tmpNormal.x = oldVbU16[nU16Base] / 65535.0;
        _tmpNormal.y = oldVbU16[nU16Base + 1] / 65535.0;
        decodeNormal(_tmpNormal);
        const [snormX, snormY] = encodeOct16(_tmpNormal.x, _tmpNormal.y, _tmpNormal.z);
        newVbU16[u16Base + 3] = ((snormX + 127) << 8) | (snormY + 127);
        // Copy remaining attributes, skipping old position (0-2) and normal slot
        for (let j = 3; j < oldStride; j++) {
            if (j === normalOldOffset)
                continue;
            const shift = j > normalOldOffset ? 2 : 1;
            newVb[newBase + (j - shift)] = oldVb[oldBase + j];
        }
    }
    mesh.vb = newVb;
    mesh.vbstride = newStride;
    if (mesh.indices) {
        const IbCtor = mesh.indices.constructor;
        const newIndices = new IbCtor(buf, vbBytes, mesh.indices.length);
        newIndices.set(mesh.indices);
        mesh.indices = newIndices;
    }
    if (mesh.iblines) {
        const IbCtor = mesh.iblines.constructor;
        const newIblines = new IbCtor(buf, vbBytes + ibBytesAligned, mesh.iblines.length);
        newIblines.set(mesh.iblines);
        mesh.iblines = newIblines;
    }
    posLayout.bytesPerItem = 2;
    posLayout.itemSize = 4;
    posLayout.normalized = true;
    delete mesh.vblayout.normal;
    const offsetShift = 2;
    for (const key in mesh.vblayout) {
        if (key !== 'position') {
            mesh.vblayout[key].offset -= offsetShift;
        }
    }
}
